import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenVisitorProfile } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';

export function LeadGenVisitorTrackingPage() {
  const [profiles, setProfiles] = useState<LeadGenVisitorProfile[]>([]);
  const { rows: paginatedProfiles, tablePaginationProps: profilesPaginationProps } =
    useClientPaginatedRows(profiles);

  const load = useCallback(async () => {
    try {
      setProfiles(await leadgenApi.listVisitorProfiles());
    } catch (err) {
      showApiError(err, 'Failed to load visitor profiles');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page-content">
      <PageHeader
        title="Visitor Session Tracking"
        description="Monitor anonymous visitor paths, referrers, and cookie IDs prior to lead conversion."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Visitor Tracking' },
        ]}
      />

      <div className="card p-5 space-y-4">
        <h3 className="section-title">Active Visitor Profiles</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                <th className="p-3">Cookie ID</th>
                <th className="p-3">IP Address</th>
                <th className="p-3">Identified Name</th>
                <th className="p-3">Email Address</th>
                <th className="p-3">Linked CRM Lead</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProfiles.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                  <td className="p-3 font-mono text-xs text-slate-600">{p.visitorCookieId}</td>
                  <td className="p-3 text-slate-700">{p.ipAddress || '—'}</td>
                  <td className="p-3 text-slate-800 font-semibold">{p.name || 'Anonymous'}</td>
                  <td className="p-3 text-slate-600">{p.email || '—'}</td>
                  <td className="p-3 text-slate-600">
                    {p.leadId ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Lead #{p.leadId}</span>
                    ) : (
                      <span className="text-xs text-slate-400">Not Converted</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.leadId ? 'bg-primary-100 text-primary-800' : 'bg-slate-100 text-slate-800'}`}>
                      {p.leadId ? 'Identified' : 'Anonymous Session'}
                    </span>
                  </td>
                </tr>
              ))}
              {profiles.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center p-6 text-slate-400">No active visitor sessions logged yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination {...profilesPaginationProps} hasRows={profiles.length > 0} />
      </div>
    </div>
  );
}
